import React, {createContext, useContext, useState, useEffect, useCallback, useRef} from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const lastKeepAliveAtRef = useRef(0);
  const keepAliveInFlightRef = useRef(false);

  useEffect(() => {
    // Check if user is logged in on initial load
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.post(`${API_CONFIG.API_URL}/auth/me`);
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Authentication error:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const onForcedLogout = () => {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('auth:logout', onForcedLogout);
    return () => window.removeEventListener('auth:logout', onForcedLogout);
  }, []);

  const keepAlive = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (keepAliveInFlightRef.current) return;

    keepAliveInFlightRef.current = true;
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.post(`${API_CONFIG.API_URL}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
      lastKeepAliveAtRef.current = Date.now();
    } catch (e) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      keepAliveInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const KEEP_ALIVE_MIN_MS = 5 * 60 * 1000;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastKeepAliveAtRef.current < KEEP_ALIVE_MIN_MS) return;
      keepAlive();
    };

    const events = ['keydown', 'mousedown', 'touchstart', 'scroll', 'pointerdown'];
    events.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [isAuthenticated, keepAlive]);

  const login = async (credentials) => {
    try {
      setError('');

      // Backend expects OAuth2 form data format for /auth/login
      const formData = new FormData();
      formData.append('username', credentials.username || credentials.email);
      formData.append('password', credentials.password);

      const response = await axios.post(`${API_CONFIG.API_URL}/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setUser(user);
      setIsAuthenticated(true);

      return user;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (userData) => {
    try {
      setError('');
      // Registration endpoint expects JSON data at /auth/register
      const response = await axios.post(`${API_CONFIG.API_URL}/auth/register`, userData);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  };

  const verifyEmail = useCallback(async (token) => {
    try {
      setError('');
      const response = await axios.get(`${API_CONFIG.API_URL}/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Email verification failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  },[]);

  const forgotPassword = async (email) => {
    try {
      setError('');
      const response = await axios.post(`${API_CONFIG.API_URL}/auth/forgot-password`, {
        email: email
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reset email. Please try again.';
      setError(message);
      throw new Error(message);
    }
  };

  const verifyResetToken = async (token) => {
    try {
      setError('');
      const response = await axios.get(`${API_CONFIG.API_URL}/auth/verify-reset-token/${token}`);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired reset token.';
      setError(message);
      throw new Error(message);
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      setError('');
      const response = await axios.post(`${API_CONFIG.API_URL}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    verifyEmail,
    forgotPassword,
    verifyResetToken,
    resetPassword,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};